import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const FriendCard = ({ friend, isOwnProfile, onRemoveFriend, onMessage }) => {
  const [showOptions, setShowOptions] = useState(false);

  return (
    <div className="bg-surface rounded-lg p-4 hover:shadow-elevation-1 transition-shadow duration-150">
      <div className="relative">
        {/* Friend Options */}
        {isOwnProfile && (
          <div className="absolute top-0 right-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowOptions(!showOptions)}
              className="w-8 h-8"
              aria-label="Friend options"
            >
              <Icon name="MoreHorizontal" size={16} />
            </Button>
            
            {showOptions && (
              <>
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setShowOptions(false)}
                  aria-hidden="true"
                />
                <div className="absolute right-0 top-8 bg-popover border border-border rounded-lg shadow-elevation-2 z-20 py-1 min-w-[120px]">
                  <button
                    onClick={() => {
                      onMessage(friend?.id);
                      setShowOptions(false);
                    }}
                    className="flex items-center w-full px-3 py-2 text-sm text-foreground hover:bg-muted"
                  >
                    <Icon name="MessageCircle" size={14} className="mr-2" />
                    Message
                  </button>
                  <button
                    onClick={() => {
                      onRemoveFriend(friend?.id);
                      setShowOptions(false);
                    }}
                    className="flex items-center w-full px-3 py-2 text-sm text-destructive hover:bg-muted"
                  >
                    <Icon name="UserMinus" size={14} className="mr-2" />
                    Remove Friend
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Friend Avatar */}
        <Link to={`/user-profile?id=${friend?.id}`} className="block">
          <div className="w-full aspect-square rounded-lg overflow-hidden bg-background mb-3 hover-lift">
            <Image
              src={friend?.avatar}
              alt={friend?.avatarAlt}
              className="w-full h-full object-cover"
            />
          </div>
        </Link>

        {/* Friend Info */}
        <div className="text-center">
          <Link 
            to={`/user-profile?id=${friend?.id}`}
            className="block hover:underline"
          >
            <h4 className="font-medium text-foreground text-sm mb-1">
              {friend?.name}
            </h4>
          </Link>
          
          <p className="text-xs text-muted-foreground mb-2">
            {friend?.mutualFriends} mutual friends
          </p>

          {/* Quick Actions */}
          <div className="flex space-x-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onMessage(friend?.id)}
              className="flex-1 text-xs"
              iconName="MessageCircle"
            >
              Message
            </Button>
            {!isOwnProfile && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {/* Handle friend action */}}
                className="w-8 h-8"
                aria-label="More actions"
              >
                <Icon name="MoreHorizontal" size={14} />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const FriendsTab = ({ friends, isOwnProfile, onRemoveFriend, onMessage }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredFriends, setFilteredFriends] = useState(friends);

  React.useEffect(() => {
    const filtered = friends?.filter(friend =>
      friend?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase())
    );
    setFilteredFriends(filtered);
  }, [searchQuery, friends]);

  if (friends?.length === 0) {
    return (
      <div className="bg-card rounded-lg shadow-elevation-1 p-8 text-center">
        <Icon name="Users" size={48} className="mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">No friends yet</h3>
        <p className="text-muted-foreground mb-4">
          {isOwnProfile 
            ? 'Start connecting with people you know!' :'This user hasn\'t added any friends yet.'
          }
        </p>
        {isOwnProfile && (
          <Button variant="default" iconName="UserPlus" iconPosition="left">
            Find Friends
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-elevation-1 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 space-y-3 sm:space-y-0">
        <h3 className="text-lg font-semibold text-foreground">
          Friends ({friends?.length})
        </h3>
        
        {/* Search Friends */}
        <div className="relative max-w-xs">
          <Icon 
            name="Search" 
            size={16} 
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            placeholder="Search friends..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            className="pl-9 pr-4 py-2 w-full bg-surface border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      </div>
      {/* Friends Grid */}
      {filteredFriends?.length === 0 ? (
        <div className="text-center py-8">
          <Icon name="Search" size={32} className="mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No friends found matching "{searchQuery}"</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {filteredFriends?.map((friend) => (
            <FriendCard
              key={friend?.id}
              friend={friend}
              isOwnProfile={isOwnProfile}
              onRemoveFriend={onRemoveFriend}
              onMessage={onMessage}
            />
          ))}
        </div>
      )}
      {/* Load More */}
      {filteredFriends?.length >= 20 && (
        <div className="mt-6 text-center">
          <Button variant="outline">
            Load More Friends
          </Button>
        </div>
      )}
    </div>
  );
};

export default FriendsTab;